/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Option as FormInputOptionProps,
  FormInputProps,
  GroupedOption,
  Option,
} from '@components/FormInput/types';
import MemoizedIcon from '@components/MemoizedIcon';
import CustomControl from '@components/SearchableCommons/CustomControl';
import CustomDropdownIndicator from '@components/SearchableCommons/CustomDropdownIndicator';
import checkIcon from '@iconify/icons-heroicons-outline/check';
import { getSelectedOption } from '@src/utils/utils';
import React, { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Select, {
  components,
  GroupBase,
  MenuPlacement,
  OptionProps,
  SelectInstance,
  SingleValue,
  StylesConfig,
} from 'react-select';
import makeAnimated from 'react-select/animated';
import { twMerge } from 'tailwind-merge';

import {
  ExtendedCustomMenuProps,
  withExtendedCustomMenuPropsHOC,
} from '../SearchableCommons/CustomMenu';

declare module 'react-select/dist/declarations/src/Select' {
  export interface Props<
    Option,
    IsMulti extends boolean,
    Group extends GroupBase<Option>,
  > {
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
  }
}

export type SearchableSelectProps = {
  onChange: (option: SingleValue<FormInputOptionProps>) => void;
  options?: FormInputOptionProps[] | GroupedOption[];
  menuPlacement?: MenuPlacement;
} & Omit<FormInputProps, 'onChange' | 'control'> &
  ExtendedCustomMenuProps;

const animatedComponents = makeAnimated();

const CustomOption = (props: OptionProps<FormInputOptionProps>) => (
  <components.Option {...props}>
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 justify-between">
      <div className="flex flex-1">
        <span
          className={twMerge(
            'flex-grow capitalize',
            props.isSelected && 'font-semibold',
          )}
        >
          {props.children}
        </span>
        {props.isSelected && !props.isDisabled && (
          <MemoizedIcon className="flex-none text-[24px]" icon={checkIcon} />
        )}
      </div>
      {props.isDisabled && props.data.disabledLabel && (
        <p className="text-black text-xs sm:text-sm">
          {props.data.disabledLabel}
        </p>
      )}
      {props.data.subLabel && (
        <p className="text-neutral-80 text-xs sm:text-sm">
          {props.data.subLabel}
        </p>
      )}
    </div>
  </components.Option>
);

const SearchableSelect = forwardRef<
  SelectInstance<FormInputOptionProps, false, GroupBase<FormInputOptionProps>>,
  SearchableSelectProps
>(
  (
    {
      name,
      placeholder,
      onChange,
      onInputChange,
      onBlur,
      isLoading,
      options = [],
      dataTestId = 'form-input',
      isClearable = true,
      isSearchable = true,
      prefix,
      suffix,
      containerClassName = '',
      value,
      isAsync,
      label,
      isBottomSheetMenu,
      bottomSheetProps,
      menuPlacement = 'auto',
      disabled,
    },
    ref,
  ) => {
    const { t } = useTranslation();

    const selectedValue = useMemo(
      () => getSelectedOption({ value, options }),
      [options, value],
    );

    const noOptionsMessage = ({ inputValue }: { inputValue: string }) => {
      return isSearchable && (inputValue.length >= 3 || options.length > 0)
        ? t('commons.searchNotFound')
        : t('commons.empty');
    };

    const CustomMenu = withExtendedCustomMenuPropsHOC({
      isBottomSheetMenu,
      bottomSheetProps: bottomSheetProps ?? {
        title: label,
      },
    });

    const handleChange = (option: SingleValue<FormInputOptionProps>) => {
      onChange?.(option);
    };

    return (
      <div data-testid={dataTestId} className={containerClassName}>
        {label && (
          <label
            className={twMerge(
              'text-sm capitalize flex items-center justify-between mb-1',
            )}
            htmlFor={name}
          >
            {label}
          </label>
        )}
        <Select
          menuPlacement={menuPlacement}
          minMenuHeight={300}
          classNames={{
            container: () =>
              'border border-neutral-70 rounded-lg bg-neutral-10 w-full',
            indicatorSeparator: () => 'hidden',
            control: () => 'p-4 [&>div]:p-0 [&>div>div]:p-0 [&>div>div]:m-0',
            singleValue: () => 'capitalize',
          }}
          styles={
            {
              control: base => ({
                ...base,
                border: 'none',
                boxShadow: 'none',
                background: 'none',
              }),
              option: (style, { isFocused, isSelected, isDisabled }) => {
                let backgroundColor = 'white';
                if (isFocused) backgroundColor = '#F0F1F3';
                if (isSelected) backgroundColor = '#EFF7FF';

                const activeColor = isSelected ? '#04559F' : 'black';
                return {
                  ...style,
                  paddingTop: 16,
                  paddingBottom: 16,
                  paddingLeft: 32,
                  paddingRight: 32,
                  textTransform: 'capitalize',
                  backgroundColor: backgroundColor,
                  color: isDisabled ? '#B4BCC7' : activeColor,
                };
              },
              group: base => ({
                ...base,
                paddingTop: 0,
                paddingBottom: 0,
              }),
              groupHeading: base => ({
                ...base,
                paddingTop: 8,
                paddingBottom: 8,
                paddingLeft: 20,
                paddingRight: 20,
                color: '#000',
                backgroundColor: '#F0F1F3',
                fontWeight: 600,
                fontSize: 14,
                marginBottom: 0,
              }),
              menuList: style => ({ ...style, padding: 0 }),
              menuPortal: base => ({ ...base, zIndex: 50 }),
              dropdownIndicator: (base, state) => ({
                ...base,
                padding: 0,
                paddingLeft: 8,
                ...(state.hasValue && isClearable && { display: 'none' }),
              }),
              menu: base => ({ ...base, position: 'relative', margin: 0 }),
            } as StylesConfig<Option, false, GroupBase<Option>>
          }
          inputId={name}
          name={name}
          placeholder={placeholder}
          closeMenuOnSelect
          menuPortalTarget={document.body}
          components={{
            ...animatedComponents,
            Control: CustomControl,
            DropdownIndicator: CustomDropdownIndicator,
            Option: CustomOption,
            Menu: CustomMenu,
          }}
          onChange={handleChange}
          onInputChange={onInputChange}
          onBlur={onBlur}
          value={selectedValue}
          options={options}
          isSearchable={isSearchable}
          isLoading={isLoading}
          isClearable={isClearable}
          noOptionsMessage={noOptionsMessage}
          {...(isAsync && {
            filterOption: () => true,
          })}
          prefix={prefix}
          suffix={suffix}
          ref={ref}
          isDisabled={disabled}
        />
      </div>
    );
  },
);

export default SearchableSelect;
